/**
 * EmotionIntelligence
 * 
 * Manages transient emotional states that overlay the session.
 * Specifically handles "Anticipation" (rising tension) and "Release".
 */

export interface EmotionHint {
    state: 'neutral' | 'anticipation' | 'release';
    registerOffset: number;      // Semitones to shift locally (0 to +24)
    velocityBoost: number;       // 0 to 0.3
    intensity: number;           // 0 to 1 (progress of the emotion)
    forceDensity?: 'dense';      // Hint to ArrangementIntelligence
}

export class EmotionIntelligence {
    private state: 'neutral' | 'anticipation' | 'release' = 'neutral';

    // State Tracking
    private progress: number = 0;       // 0 to 1 (normalized duration)
    private durationTarget: number = 0; // planned duration in touches for current state
    private touchCountInState: number = 0;

    // Config
    private readonly ANTICIPATION_CHANCE = 0.005; // ~0.5% chance per touch (rare event)
    private readonly MIN_TOUCHES_BETWEEN_EVENTS = 100; // Cool down
    private lastEventEndTouch: number = -100;

    constructor() {
        this.reset();
    }

    reset() {
        this.state = 'neutral';
        this.progress = 0;
        this.touchCountInState = 0;
        this.lastEventEndTouch = -100;
        this.durationTarget = 1; // Safety non-zero
    }

    /**
     * Get the current emotional state and modifiers.
     * Call this BEFORE generating notes.
     */
    getHint(globalTouchCount: number): EmotionHint {
        // State Machine Update
        this.updateState(globalTouchCount);

        let registerOffset = 0;
        let velocityBoost = 0;
        let intensity = 0;
        let forceDensity: 'dense' | undefined = undefined;

        if (this.state === 'anticipation') {
            intensity = this.progress; // 0 -> 1

            // Rise: Linear or Exponential pitch shift up to +24 semitones (2 octaves)
            // We want it to be subtle at first, then obvious.
            // Let's go linear for now, snapping to semitones.
            registerOffset = Math.floor(intensity * 24);

            // Velocity boost: 0 -> 0.3
            velocityBoost = intensity * 0.3;

            forceDensity = 'dense'; // Build wall of sound

        } else if (this.state === 'release') {
            intensity = 1.0 - this.progress; // 1 -> 0

            // Fall: Return to 0 from wherever we were
            // Rapid descent
            registerOffset = Math.floor(intensity * 24);

            // Velocity boost maintains slightly during release for impact
            velocityBoost = intensity * 0.2;
        }

        return {
            state: this.state,
            registerOffset: isNaN(registerOffset) ? 0 : registerOffset,
            velocityBoost: isNaN(velocityBoost) ? 0 : velocityBoost,
            intensity: isNaN(intensity) ? 0 : intensity,
            forceDensity
        };
    }

    private updateState(globalTouchCount: number) {
        if (this.state === 'neutral') {
            // Check if we can trigger anticipation
            // 1. Cooldown check
            if (globalTouchCount - this.lastEventEndTouch < this.MIN_TOUCHES_BETWEEN_EVENTS) {
                return;
            }

            // 2. Random chance
            if (Math.random() < this.ANTICIPATION_CHANCE) {
                this.startAnticipation();
            }

        } else if (this.state === 'anticipation') {
            this.touchCountInState++;

            if (this.touchCountInState >= this.durationTarget) {
                // Done rising, switch to release
                this.startRelease();
            } else {
                this.progress = this.touchCountInState / this.durationTarget;
            }

        } else if (this.state === 'release') {
            this.touchCountInState++;

            if (this.touchCountInState >= this.durationTarget) {
                // Done releasing, back to neutral
                this.state = 'neutral';
                this.lastEventEndTouch = globalTouchCount;
                this.progress = 0;
            } else {
                this.progress = this.touchCountInState / this.durationTarget;
            }
        }
    }

    private startAnticipation() {
        this.state = 'anticipation';
        this.touchCountInState = 0;
        this.progress = 0;
        // Random duration between 16 and 32 touches (a nice long build up)
        this.durationTarget = 16 + Math.floor(Math.random() * 16);

        if (process.env.NODE_ENV === 'development') {
            console.log(`[EmotionIntelligence] Anticipation Started (Duration: ${this.durationTarget} touches)`);
        }
    }

    private startRelease() {
        this.state = 'release';
        this.touchCountInState = 0;
        this.progress = 0;
        // Faster release than build up: 8 to 12 touches
        this.durationTarget = 8 + Math.floor(Math.random() * 4);

        if (process.env.NODE_ENV === 'development') {
            console.log(`[EmotionIntelligence] Anticipation Release Started (Duration: ${this.durationTarget} touches)`);
        }
    }
}
